'use client';

import { useEffect, useState } from 'react';
import api from '@/lib/axios';
import ProductCard from '@/components/product/ProductCard';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';

interface Kategori {
  id: number; ad: string; slug: string; parent_id: number | null;
  alt_kategoriler?: Kategori[];
}

interface Urun {
  id: number; urun_ad: string; marka: string; urun_kdv: number; urun_slug: string;
  urun_resimleri: { dosya_yolu: string; alt_metin: string }[];
  urun_varyasyonlari: { id: number; varyasyon_ad: string; satis_fiyati: number; piyasa_fiyati: number; stok: number }[];
  kategori: { ad: string; slug: string };
}

export default function KategoriSayfasi({ kategori: ilkKategori }: { kategori: Kategori }) {
  const [kategori] = useState<Kategori>(ilkKategori);
  const [ustKategoriler, setUstKategoriler] = useState<Kategori[]>([]);
  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [toplam, setToplam] = useState(0);
  const [sayfa, setSayfa] = useState(1);

  useEffect(() => {
    api.get('/kategoriler').then((res) => {
      const tumKat: Kategori[] = res.data;
      const zincir: Kategori[] = [];
      let current = ilkKategori;
      while (current.parent_id) {
        const parent = tumKat.find(k => k.id === current.parent_id);
        if (parent) { zincir.unshift(parent); current = parent; } else break;
      }
      setUstKategoriler(zincir);
    });
  }, [ilkKategori.id]);

  useEffect(() => {
    setYukleniyor(true);
    api.get(`/urunler?kategori_id=${kategori.id}&sayfa=${sayfa}&limit=20`).then((res) => {
      setUrunler(res.data.veri || []);
      setToplamSayfa(res.data.meta?.toplam_sayfa || 1);
      setToplam(res.data.meta?.toplam || 0);
      setYukleniyor(false);
    });
  }, [kategori.id, sayfa]);

  const urunUrl = (urun: Urun) => {
    if (urun.urun_slug) return `/${urun.urun_slug}`;
    return `/urunler/${urun.id}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1 text-sm text-[var(--color-gray-500)] mb-6 flex-wrap">
        <Link href="/" className="hover:text-[var(--color-primary)]">Ana Sayfa</Link>
        {ustKategoriler.map((ust) => (
          <span key={ust.id} className="flex items-center gap-1">
            <ChevronRight className="w-4 h-4" />
            <Link href={`/${ust.slug}`} className="hover:text-[var(--color-primary)]">{ust.ad}</Link>
          </span>
        ))}
        <ChevronRight className="w-4 h-4" />
        <span className="text-[var(--color-gray-800)] font-medium">{kategori.ad}</span>
      </nav>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--color-gray-800)]">{kategori.ad}</h1>
        <span className="text-sm text-[var(--color-gray-500)]">{toplam} ürün</span>
      </div>

      {kategori.alt_kategoriler && kategori.alt_kategoriler.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {kategori.alt_kategoriler.map((alt) => (
            <Link key={alt.id} href={`/${alt.slug}`}
              className="px-4 py-2 bg-white border border-[var(--color-gray-200)] rounded-[var(--radius-full)] text-sm text-[var(--color-gray-700)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition shadow-[var(--shadow-sm)]">
              {alt.ad}
            </Link>
          ))}
        </div>
      )}

      {yukleniyor ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-[var(--color-gray-200)] animate-pulse rounded-[var(--radius-xl)] h-72" />
          ))}
        </div>
      ) : urunler.length === 0 ? (
        <div className="text-center py-20 text-[var(--color-gray-500)]">Bu kategoride henüz ürün bulunmuyor.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {urunler.map((urun) => (
              <ProductCard
                key={urun.id}
                id={urun.id}
                urun_ad={urun.urun_ad}
                marka={urun.marka}
                urun_kdv={urun.urun_kdv}
                resim={urun.urun_resimleri?.[0]?.dosya_yolu}
                resim_alt={urun.urun_resimleri?.[0]?.alt_metin}
                resimler={urun.urun_resimleri}
                varyasyonlar={urun.urun_varyasyonlari}
                kategori={urun.kategori?.ad}
                href={urunUrl(urun)}
              />
            ))}
          </div>
          {toplamSayfa > 1 && (
            <div className="flex justify-center gap-2 mt-8 flex-wrap">
              <button onClick={() => setSayfa(Math.max(1, sayfa - 1))} disabled={sayfa === 1}
                className="px-4 py-2 rounded-[var(--radius-lg)] text-sm border border-[var(--color-gray-200)] hover:border-[var(--color-primary)] disabled:opacity-50 transition">
                ← Önceki
              </button>
              {[...Array(toplamSayfa)].map((_, i) => (
                <button key={i} onClick={() => setSayfa(i + 1)}
                  className={`w-10 h-10 rounded-[var(--radius-lg)] text-sm font-medium transition ${sayfa === i + 1 ? 'bg-[var(--color-primary)] text-white' : 'bg-white border border-[var(--color-gray-200)] text-[var(--color-gray-700)] hover:border-[var(--color-primary)]'}`}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setSayfa(Math.min(toplamSayfa, sayfa + 1))} disabled={sayfa === toplamSayfa}
                className="px-4 py-2 rounded-[var(--radius-lg)] text-sm border border-[var(--color-gray-200)] hover:border-[var(--color-primary)] disabled:opacity-50 transition">
                Sonraki →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}